import { Grid, Typography } from '@mui/material';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { ICorrespondenceViewModel, ISpecialtyViewModel } from 'viewModels';
import { useTheme } from 'styled-components';
import { IRefRegisterTypeServiceData, IRegisterTypeServiceData } from 'entities';
import { useRegisterForm } from '../../context';
import { Accordion } from '../Accordion';
import { ButtonStyled, Container, Content, TableMap } from './styles';
import { Button } from '../Buttons/Button';
import { useSpecialties } from '../../context/specialties';
import { useCorrespondences } from '../../context/correspondences';

export const RegisterTypeServices = forwardRef<IRefRegisterTypeServiceData>((_, ref) => {
  const refComponent = useRef<HTMLDivElement>(null);
  const {
    typeProfile,
    activeForm,
    setActiveForm,
    formSubmited,
    isServiceProviderCurrentSelection,
  } = useRegisterForm();
  const { colors } = useTheme();
  const location = useLocation();
  const { correspondences, correspondencesSelectedByUser } = useCorrespondences();
  const {
    specialties,
    specialtiesSelectedByUser,
    hasNextPageSpecialty,
    hasNextPageSpecialtiesSelectedByUser,
    isLoadingSpecialties,
    isLoadingSpecialtiesSelectedByUser,
    isFetchingNextPageSpecialtiesSelectedByUser,
    isFetchingNextPageSpecialty,
    handleFetchNextPageSpecialty,
  } = useSpecialties();
  const [displaySummary, setDisplaySummary] = useState(true);
  const [isLoadingItens, setLoadingItens] = useState<boolean | undefined>(false);
  const [specialtiesSelected, setSpecialtiesSelected] =
    useState<ISpecialtyViewModel[]>(specialtiesSelectedByUser);
  const [correspondencesSelected, setCorrespondencesSelected] = useState<
    ICorrespondenceViewModel[]
  >(correspondencesSelectedByUser);
  const [correspondencesFiltered, setCorrespondencesFiltered] =
    useState<ICorrespondenceViewModel[]>(correspondences);
  const color =
    location.pathname === '/naturalPersonAccount' || location.pathname === '/legalPersonAccount'
      ? 'background'
      : 'background_white';

  const handleSelectSpecialty = (specialty: ISpecialtyViewModel) => {
    const isExists = specialtiesSelected.some((s) => s.id === specialty.id);
    if (isExists) setSpecialtiesSelected((prev) => prev.filter((s) => s.id !== specialty.id));
    else setSpecialtiesSelected((prev) => [...prev, specialty]);
  };
  const handleSelectCorrespondence = (correspondence: ICorrespondenceViewModel) => {
    const isExists = correspondencesSelected.some((c) => c.id === correspondence.id);
    if (isExists)
      setCorrespondencesSelected((prev) => prev.filter((c) => c.id !== correspondence.id));
    else setCorrespondencesSelected((prev) => [...prev, correspondence]);
  };

  const handleSelectAllSpecialties = () => {
    if (!specialties) return;
    if (specialtiesSelected.length === specialties.length) {
      setSpecialtiesSelected([]);
    } else {
      setSpecialtiesSelected(specialties);
    }
  };

  const handleSelectAllCorrespondences = () => {
    if (!correspondencesSelected) return;

    if (correspondencesSelected.length === correspondences.length) {
      setCorrespondencesSelected([]);
    } else {
      setCorrespondencesSelected(correspondences);
    }
  };

  const handleHiddenSummary = () => {
    setDisplaySummary(!displaySummary);
    setActiveForm('registerTypeService');
  };

  const handleFocusOnError = () => {
    refComponent.current?.scrollIntoView({ behavior: 'smooth' });
  };
  const handleUpdateTypeServiceData = () => {
    const hasError =
      isServiceProviderCurrentSelection &&
      (!specialtiesSelected.length || !correspondencesSelected.length);
    const newData: IRegisterTypeServiceData = {
      specialtiesSelected,
      correspondencesSelected,
      hasError,
    };
    return newData;
  };

  const handleTourDefaultValues = () => {
    setDisplaySummary(true);
  };

  useImperativeHandle(ref, () => ({
    handleTourDefaultValues,
    handleUpdateTypeServiceData,
    handleFocusOnError,
  }));

  useEffect(() => {
    if (activeForm === 'registerTypeService') {
      setDisplaySummary(false);
    } else {
      setDisplaySummary(true);
    }
  }, [activeForm]);

  useEffect(() => {
    if (!specialtiesSelected.length) {
      setSpecialtiesSelected(specialtiesSelectedByUser);
    }

    if (!correspondencesSelected.length) {
      setCorrespondencesSelected(correspondencesSelectedByUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [correspondencesSelectedByUser, specialtiesSelectedByUser]);

  useEffect(() => {
    if (correspondences) {
      setCorrespondencesFiltered(correspondences);
    }
  }, [correspondences]);

  useEffect(() => {
    if (correspondencesSelectedByUser.length > 0) {
      setCorrespondencesSelected(correspondencesSelectedByUser);
    }
  }, [correspondencesSelectedByUser]);

  useEffect(() => {
    setLoadingItens(
      isLoadingSpecialties ||
        isLoadingSpecialtiesSelectedByUser ||
        isFetchingNextPageSpecialtiesSelectedByUser ||
        isFetchingNextPageSpecialty,
    );
  }, [
    isFetchingNextPageSpecialtiesSelectedByUser,
    isFetchingNextPageSpecialty,
    isLoadingSpecialties,
    isLoadingSpecialtiesSelectedByUser,
  ]);

  return (
    <Container color={color} ref={refComponent}>
      {!displaySummary && (
        <Content>
          <div>
            <Accordion
              title="Suas especialidades"
              subtitle="(Selecione abaixo as suas especialidades)"
              isOpen
              activeButton
              onCallback={() => {
                handleSelectAllSpecialties();
              }}
              type="default">
              <TableMap>
                <div className="contentTable">
                  {specialties?.map((s) => (
                    <div key={s.id}>
                      <ButtonStyled
                        type="button"
                        onClick={() => handleSelectSpecialty(s)}
                        isActive={specialtiesSelected.some((specialty) => specialty.id === s.id)}>
                        {s.name}
                      </ButtonStyled>
                    </div>
                  ))}
                </div>
              </TableMap>
              {(hasNextPageSpecialty || hasNextPageSpecialtiesSelectedByUser) && (
                <Grid container justifyContent="center" padding="1rem 1rem">
                  <Button
                    text="Carregar mais..."
                    onClick={() => handleFetchNextPageSpecialty()}
                    dimensions={{ height: '2rem', width: '10rem' }}
                    color="primary"
                    textHoverColor="tertiary_500"
                    backgroundHoverColor="transparent"
                    background="transparent"
                    border={{ color: 'transparent' }}
                    loading={isLoadingItens}
                    disabled={isLoadingItens}
                  />
                </Grid>
              )}
            </Accordion>
            {formSubmited && isServiceProviderCurrentSelection && !specialtiesSelected.length && (
              <span className="fieldError">Selecione pelo menos uma especialidade</span>
            )}
          </div>
          <div>
            <Accordion
              title="Serviços de correspondência"
              subtitle="(Selecione abaixo as suas especialidades)"
              isOpen
              activeButton
              onCallback={() => handleSelectAllCorrespondences()}
              type="default">
              <TableMap>
                <div className="contentTable">
                  {correspondencesFiltered ? (
                    correspondencesFiltered
                      ?.sort((a, b) => a.name.localeCompare(b.name))
                      .map((c) => (
                        <div key={c.id}>
                          <ButtonStyled
                            type="button"
                            onClick={() => handleSelectCorrespondence(c)}
                            isActive={correspondencesSelected.some(
                              (correspondece) => correspondece.id === c.id,
                            )}>
                            {c.name}
                          </ButtonStyled>
                        </div>
                      ))
                  ) : (
                    <Grid container padding="1rem" justifyContent="center">
                      <Typography justifyContent="center" align="center" color={colors.primary}>
                        Buscando serviços de correspondência, por favor aguarde......
                      </Typography>
                    </Grid>
                  )}
                </div>
              </TableMap>
            </Accordion>
            {formSubmited &&
              isServiceProviderCurrentSelection &&
              !correspondencesSelected.length && (
                <span className="fieldError">
                  Selecione pelo menos um serviço de correspondência
                </span>
              )}
          </div>
        </Content>
      )}
      {displaySummary && (
        <Content>
          <div className="summaryItens" id="select-specialties">
            <button onClick={handleHiddenSummary} type="button" className="titleSummary">
              Suas especialidades
            </button>
            <div className="contentTableSummary">
              {specialtiesSelected.map((s) => (
                <div key={s.id}>
                  <ButtonStyled
                    type="button"
                    onClick={handleHiddenSummary}
                    isActive={specialtiesSelected.some((specialty) => specialty.id === s.id)}>
                    {s.name}
                  </ButtonStyled>
                </div>
              ))}
            </div>
          </div>
          <div className="summaryItens" id="select-correspondences">
            <button onClick={handleHiddenSummary} type="button" className="titleSummary">
              Serviços de correspondência
            </button>
            <div className="contentTableSummary">
              {correspondencesSelected
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((c) => (
                  <div key={c.id}>
                    <ButtonStyled
                      type="button"
                      onClick={handleHiddenSummary}
                      isActive={correspondencesSelected.some(
                        (correspondence) => correspondence.id === c.id,
                      )}>
                      {c.name}
                    </ButtonStyled>
                  </div>
                ))}
            </div>
          </div>
        </Content>
      )}
    </Container>
  );
});
